import React from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import styled from "../../component/commonCard/style.module.css";
import { AXIS_TICK, GRID_STROKE, TOOLTIP_STYLE } from "../../component/commonCard/parts/theme";

// Generic multi-series area/line trend chart. `series` = [{ key, name, color, fillOpacity? }].
const TrendChart = ({ title, data, series, xKey = "date", height = 240, emptyHint }) => {
  const hasData = Array.isArray(data) && data.length > 0;
  // The Y axis has no fixed domain -- it auto-scales from these values -- but
  // if the API sends a series value as a numeric string (common with SQL
  // aggregation results), Recharts' domain/point calculations can silently
  // misbehave and clip anything above whatever it picked as "max". Coerce
  // every series value to a real number so a day with e.g. 150 views always
  // plots correctly instead of only showing up when values stay small/single
  // digit by coincidence.
  const normalizedData = hasData
    ? data.map((row) => {
        const next = { ...row };
        series.forEach((s) => {
          if (next[s.key] != null) next[s.key] = Number(next[s.key]) || 0;
        });
        return next;
      })
    : data;
  return (
    <div className={styled.chartCard}>
      <div className={styled.chartCardHead}>
        <h3 className={styled.chartTitle}>{title}</h3>
      </div>
      {hasData ? (
        <ResponsiveContainer width="100%" height={height} minWidth={0}>
          <AreaChart data={normalizedData} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey={xKey} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={40} allowDecimals={false} domain={[0, "auto"]} />
            <Tooltip {...TOOLTIP_STYLE} />
            {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} />}
            {series.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fill={s.fillOpacity === 0 ? undefined : `url(#grad-${s.key})`}
                fillOpacity={s.fillOpacity === 0 ? 0 : undefined}
                dot={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <p className={styled.emptyHint}>{emptyHint || "No data yet."}</p>
      )}
    </div>
  );
};

export default TrendChart;
